import React from "react";
import { Model, Item } from "../types";

interface TTSModalProps {
  isOpen: boolean;
  model: Model | null;
  items?: Item[];
  onClose: () => void;
}

const colorFor = (key: string) => key;

const wrapBold = (text: string) => `[b]${text}[/b]`;
const wrapColor = (hex: string, text: string) => `[${hex}]${text}[-]`;

const categoryStyle = (item: Item) => {
  const name = item.name.toLowerCase();
  const k = (item.category || "").toLowerCase();
  // Heuristics for style
  if (k === "melee" || /sword|axe|knife|bayonet/.test(name)) return { color: 'fa8231', symbol: '✞' };
  if (k === "ranged" || /pistol|rifle|shotgun|gun|revolver|sniper/.test(name)) return { color: 'C966E5', symbol: '⊕' };
  if (k === "armour") return { color: 'b2b7be', symbol: '❖' };
  if (k === "gear" || /binocular|binoc|goggles/.test(name)) return { color: 'f7b731', symbol: '✜' };
  if (/grenade|frag|blast/.test(name) || item.keywords?.some(kw => /GRENADE|BLAST/.test(kw))) return { color: 'fa8231', symbol: '✢' };
  if (/shield/.test(name) || item.keywords?.some(kw => /SHIELD/.test(kw))) return { color: 'fa8231', symbol: '☩' };
  return { color: 'ffffff', symbol: '•' };
};

const generateModelText = (model: Model, items?: Item[]) => {
  const lines: string[] = [];

  // Title with decoration
  lines.push(wrapBold(`☩☩ ${model.name} ☩☩`));

  // Class line
  lines.push(`(${model.class})`);

  // Stats line: movement/class, melee modifier, ranged modifier, armour
  const statParts: string[] = [];
  if (model.stats) {
    statParts.push(wrapBold(wrapColor('89E1E3', `${model.stats.movement}\"/${model.class}`)));
    // check for stat modifiers in keywords or abilities (simple heuristic)
    // For this example, include melee/ranged/armour from stats differences
    statParts.push(wrapBold(wrapColor('fa8231', `Mel. ${model.stats.melee}${model.stats.melee >= 0 ? 'D' : 'D'}`)));
    statParts.push(wrapBold(wrapColor('C966E5', `Ran. ${model.stats.ranged}${model.stats.ranged >= 0 ? 'D' : 'D'}`)));
    statParts.push(wrapBold(wrapColor('b2b7be', `Arm. ${model.stats.finalArmour >= 0 ? `+${model.stats.finalArmour - model.stats.baseArmour || 0}` : `${model.stats.finalArmour - model.stats.baseArmour || 0}`}`)));
  }
  if (statParts.length) lines.push(statParts.join(' | '));

  // Faction/type line: use keywords or type
  const faction = (model.keywords || []).find(k => k.toUpperCase() === k && k.length > 2) || '';
  const typeLabel = model.type ? model.type.toUpperCase() : '';
  const line3 = [faction, typeLabel].filter(Boolean).join(', ');
  if (line3) lines.push(line3);

  lines.push('');

  // Items
  if (model.items?.length) {
    for (const itemRef of model.items) {
      const found = items?.find(i => i.id === itemRef);
      const item = found ? found : { id: itemRef, name: itemRef, category: '', keywords: [] } as Item;
      const style = categoryStyle(item);
      lines.push(wrapBold(wrapColor(style.color, `${style.symbol} ${item.name}`)));
      // details line
      const details: string[] = [];
      if (item.range) details.push(item.range);
      if (item.keywords && item.keywords.length) details.push(item.keywords.join(', '));
      if (details.length) lines.push(details.join(' / '));
      if (item.notes) lines.push(item.notes);
    }

    lines.push('');
  }

  // Abilities
  if (model.abilities?.length) {
    for (const a of (model.abilities as any[])) {
      if (typeof a === 'string') {
        lines.push(wrapBold(wrapColor('0fb9b1', `✧ ${a}`)));
      } else {
        lines.push(wrapBold(wrapColor('0fb9b1', `✧ ${a.name}`)));
        if (a.description) lines.push(a.description);
      }
    }
    lines.push('');
  }

  // Skills
  if (model.skills?.length) {
    for (const s of model.skills) {
      lines.push(wrapBold(wrapColor('618DF4', `✹ ${s.name}${s.table ? ` (${s.table})` : ''}`)));
      if (s.description) lines.push(s.description);
    }
    lines.push('');
  }

  // Keywords / traits
  if (model.keywords?.length) {
    for (const kw of model.keywords) {
      const negative = /shell|shock|fear|down|injury|wound|capture|dead|panic/i.test(kw);
      const color = negative ? 'eb3b5a' : '20bf6b';
      const symbol = negative ? '▽' : '△';
      lines.push(wrapBold(wrapColor(color, `${symbol} ${kw}`)));
    }
  }

  return lines.join('\n');
};

export const TTSModal = ({ isOpen, model, items, onClose }: TTSModalProps) => {
  if (!isOpen || !model) return null;

  const text = generateModelText(model, items);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      // small visual feedback could be added later
    } catch (e) {
      // fallback: select textarea
      // noop
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-3">
      <div className="ledger-shell max-w-2xl w-full max-h-[80vh] overflow-y-auto text-sm p-4">
        <div className="flex justify-between items-start mb-3">
          <h3 className="font-semibold">TTS - {model.name}</h3>
          <div className="flex gap-2">
            <button onClick={handleCopy} className="ledger-btn px-3 py-1 text-sm">Copy</button>
            <button onClick={onClose} className="ledger-btn px-3 py-1 text-sm">Close</button>
          </div>
        </div>
        <textarea
          readOnly
          value={text}
          className="w-full h-64 bg-gray-900/90 text-gray-200 p-3 rounded border border-gray-700 resize-none"
        />
      </div>
    </div>
  );
};

export default TTSModal;
